import type { ViewQueryRecordNode, ViewQueryRecordNodeWithId } from '@salescore/core'
import { Array, HashMap, Option, pipe } from 'effect'
import { type Dispatch, useCallback, useMemo } from 'react'
import { atom, useRecoilValue, useSetRecoilState } from 'recoil'

//
// records関連
//
export const recordIdsAtom = atom<string[]>({
  key: 'recordIds',
  default: [],
})

export const recordsImplAtom = atom<HashMap.HashMap<string, ViewQueryRecordNodeWithId>>({
  key: 'record',
  default: HashMap.empty(),
})

export const useRecordsImpl = (): ViewQueryRecordNodeWithId[] => {
  const recordIds = useRecoilValue(recordIdsAtom)
  const records = useRecoilValue(recordsImplAtom)
  return useMemo(
    () =>
      pipe(
        recordIds,
        Array.flatMapNullishOr((id) => HashMap.get(records, id).pipe(Option.getOrUndefined)),
      ),
    [recordIds, records],
  )
}

export const convertForRecordsImpl = (
  recordNodes: ViewQueryRecordNode[],
): {
  recordIds: string[]
  recordsImpl: HashMap.HashMap<string, ViewQueryRecordNodeWithId>
} => {
  const newRecords = Array.dedupeWith(recordNodes, (a, b) => a.id === b.id) // dedupeWithは不要なはずだが、不具合で重複してしまうことに備えてdedupeWithしておく
  const recordIds = Array.flatMapNullishOr(newRecords, (record) => record.id)
  const recordsImpl = pipe(
    newRecords,
    Array.flatMapNullishOr((record): [string, ViewQueryRecordNodeWithId] | undefined =>
      record.id === undefined ? undefined : [record.id, record],
    ),
    HashMap.fromIterable,
  )
  return { recordIds, recordsImpl }
}

export const useSetRecordsImpl = (): Dispatch<ViewQueryRecordNode[]> => {
  const setRecordIds = useSetRecoilState(recordIdsAtom)
  const setRecordsImpl = useSetRecoilState(recordsImplAtom)
  return useCallback(
    (newValue: ViewQueryRecordNode[]) => {
      const { recordIds, recordsImpl } = convertForRecordsImpl(newValue)
      setRecordIds(recordIds)
      setRecordsImpl(recordsImpl)
    },
    [setRecordIds, setRecordsImpl],
  )
}

export type UpdateRecordsImplArgument = Array<{
  id: string
  value: ViewQueryRecordNodeWithId | undefined
}>

export const useUpdateRecordsImpl = (): ((updates: UpdateRecordsImplArgument) => void) => {
  const setRecordsImpl = useSetRecoilState(recordsImplAtom)
  return useCallback(
    (updates: UpdateRecordsImplArgument): void => {
      setRecordsImpl((previous) =>
        pipe(
          updates,
          Array.reduce(previous, (current, { id, value }) =>
            value === undefined ? HashMap.remove(current, id) : HashMap.set(current, id, value),
          ),
        ),
      )
    },
    [setRecordsImpl],
  )
}
